import { useContext, useEffect, useMemo, useState, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CurrencyInput from 'react-currency-input-field';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { LIMITS_TYPES, SupplierPickerRoute } from '../constants';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { ENTER_KEY } from '../../../constants/general';
import { ReactComponent as IconBank } from '../../../assets/svg/shared/payment-methods/icon-bank.svg';
import { useStore } from '../../../store';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { AchOutContext } from '../pages';
import { IAchOutLimits, useGetAchOutLimits } from '../../../hooks/query/useGetAchOutLimits';
import { ReactComponent as IconBackArrow } from '../../../assets/svg/shared/icon-arrow-back.svg';
import { VIEWS_MAP } from '../../../router/constants';
import { SUPPLIER_REFERRER_TYPES } from '../../Supplier/consts';

interface IProps {
  isEditMode?: boolean;
  handleEditMode?: (type: string) => void;
  onSubmit: () => void;
}

const getLowestLimit = (achLimits: IAchOutLimits) => {
  const sortingArray: any = [];
  Object.keys(achLimits).forEach((key) => {
    sortingArray.push({ key, value: achLimits[key] });
  });
  sortingArray.sort((a: { key: string; value: number }, b: { key: string; value: number }) => a.value - b.value);
  return sortingArray[0];
};

const AmountAchOut = ({ onSubmit, isEditMode, handleEditMode }: IProps) => {
  const FlowContext = useContext(AchOutContext);
  const { isLoading } = FlowContext;
  const {
    user: { customerId },
    getPersonalAccount,
  } = useStore((state) => state);
  const { accountExternalId } = getPersonalAccount();
  const transferAmount = useStore((state) => state.transferAmount);
  const setTransferAmount = useStore((state) => state.setTransferAmount);
  const [inputAmount, setInputAmount] = useState<string>(transferAmount);
  const [lowestLimit, setLowestLimit] = useState<{ key: string; value: number }>({ key: '', value: 0 });
  const [dynamicContent, setDynamicContent] = useState<{ title: string; errorMsg: string }>({
    title: '',
    errorMsg: '',
  });
  const [formError, setFormError] = useState<boolean>(false);
  const { balance } = useStore((state) => state.getPrimaryAccount());
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { refetch: getAchOutLimits, isRefetching: isLimitsRefetching } = useGetAchOutLimits(
    {
      customerId,
      accountExternalId,
    },
    (achLimitsResponse: IAchOutLimits) => {
      setLowestLimit(getLowestLimit(achLimitsResponse));
    },
    () => {},
  );

  const isFormValid = useMemo(() => {
    if (!inputAmount || inputAmount === '0') {
      setFormError(false);
      return false;
    }
    if (Number(inputAmount) > lowestLimit.value) {
      setFormError(true);
      return false;
    }
    setFormError(false);
    return true;
  }, [inputAmount, lowestLimit.key]);

  const handleCancel = () => {
    if (handleEditMode) {
      handleEditMode('');
    }
  };

  function getDynamicContent() {
    switch (lowestLimit.key) {
      case LIMITS_TYPES.BALANCE:
        setDynamicContent({
          title: t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.LIMITS.BALANCE.TITLE'),
          errorMsg: t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.LIMITS.BALANCE.ERROR'),
        });
        break;

      case LIMITS_TYPES.DAILY:
        setDynamicContent({
          title: t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.LIMITS.DAILY.TITLE'),
          errorMsg: t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.LIMITS.DAILY.ERROR'),
        });
        break;

      case LIMITS_TYPES.MONTHLY:
        setDynamicContent({
          title: t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.LIMITS.MONTHLY.TITLE'),
          errorMsg: t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.LIMITS.MONTHLY.ERROR'),
        });
        break;

      default:
        setDynamicContent({ title: 'N/A', errorMsg: 'N/A' });
    }
  }

  const handleBackClick = () => {
    if (!isEditMode) {
      setTransferAmount('');
      navigate(SupplierPickerRoute, {
        state: {
          referrer: SUPPLIER_REFERRER_TYPES.ACH_OUT,
          closeUrl: `/${VIEWS_MAP.DASHBOARD}`,
        },
      });
    }
  };
  const renderPageHeader = () => (
    <div className='relative flex'>
      <button
        type='button'
        className='absolute left-[-221px] top-[9px] flex cursor-pointer'
        onClick={handleBackClick}
        data-testid='achOutTransferAmountBackButton'
      >
        <IconBackArrow className='relative top-[3px]' />
        <div className='text-bw08 text-md font-metricRMedium inline-block h-[24px] border-b-[1px] border-bw08'>
          {t('ACH_OUT_NEW.BACK')}
        </div>
      </button>
      <Heading
        testId='achDetailsTitle'
        title={
          isEditMode
            ? t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.TITLE_EDIT_MODE')
            : t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.REDESIGN.TITLE')
        }
        className='font-metricRMedium text-[28px] text-bw08 mb-[112px]'
      />
    </div>
  );

  useEffect(() => {
    if (!lowestLimit.key) return;
    getDynamicContent();
  }, [lowestLimit.key]);

  useEffect(() => {
    trackMixPanel('View ACH to 3rd Party - Enter Amount Screen');
    getAchOutLimits();
  }, []);
  return (
    <div
      className={`flex flex-col w-full h-full items-center ${isEditMode ? 'animate-fadeIn' : ''} rounded-[8px] bg-bw00 pt-[64px]`}
    >
      <div className='w-full grow flex flex-col items-center pt-[64px] relative'>
        {!isEditMode ? renderPageHeader() : null}
        <CurrencyInput
          id='inputAmountToTransfer'
          name='input-name'
          onKeyDown={(event: KeyboardEvent) => {
            if (!formError && event.key === ENTER_KEY) onSubmit();
          }}
          className='w-[260px]  h-[50px] outline-none font-metricRMedium text-[42px] text-bw07 text-center placeholder-bw04 border-b caret-action03 border-action03 mb-[16px]'
          placeholder='$0.00'
          defaultValue={Number(inputAmount) || 0}
          decimalsLimit={2}
          prefix='$'
          maxLength={9}
          autoFocus
          disabled={isLimitsRefetching}
          onValueChange={(value) => setInputAmount(value || '')}
        />
        {formError && (
          <Heading
            testId='achOutAmountPageInputError'
            title={dynamicContent.errorMsg}
            className='font-metricMedium mx-auto text-[12px] text-red02 mb-[16px]'
          />
        )}
        <div className='mt-[65px] w-[375px] flex justify-between'>
          <div className='flex items-center'>
            <IconBank />
            <div className='ml-[5px] text-md text-bw07 font-metricRMedium'>
              {t('BANK_TRANSFER_REDESIGN.AMOUNT_STEP.CHECKING_ACCOUNT')}
            </div>
          </div>
          <div>
            <div className='text-[12px] text-bw07 font-metricR'>
              {t('BANK_TRANSFER_REDESIGN.AMOUNT_STEP.AVAILABLE_BALANCE')}
            </div>
            <div
              data-testid='achOutAmountPageAvailableBalance'
              className='text-md text-action03 font-metricRMedium text-right'
            >
              {convertValueToUSD(balance, 2)}
            </div>
          </div>
        </div>
      </div>
      <div className='w-full flex items-center justify-center py-[24px] border-t border-bw03 fadeIn'>
        <Button
          isLoading={isLoading}
          onClick={() => {
            setTransferAmount(inputAmount);
            onSubmit();
          }}
          disable={!isFormValid || isLoading}
          title={
            isEditMode
              ? t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.CTA_MAIN_EDIT_MODE')
              : t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.CTA_MAIN')
          }
          className={`${isFormValid ? 'bg-action03 hover:bg-action04 active:bg-action04' : 'bg-bw05'} ${isEditMode ? 'mb-[16px]' : ''} max-w-[100%] w-[375px] h-[54px] mt-auto mx-auto text-md text-bw00 rounded font-metricRMedium`}
          id='handleReviewCTA'
        />
        {isEditMode && (
          <Button
            onClick={handleCancel}
            title={t('ACH_OUT_REVIEW.ACH_OUT_AMOUNT.CTA_CANCEL_EDIT_MODE')}
            className='font-metric text-[14px] text-bw06'
            id=''
          />
        )}
      </div>
    </div>
  );
};

export default AmountAchOut;
